/**
 * Merge a note onto what a cycle outcome already stores.
 *
 * A correction replaces the whole record, and any writer that sends `notes` at
 * all overwrites that column — so it has to send back what was there or destroy
 * it. (Fields the write does not carry survive; `notes` is the one being
 * written, which is exactly why it needs merging.) That matters more than it
 * sounds: `RecordBetaResultModal` keeps the raw beta hCG value as the first line
 * of `notes`, because no backend field models it. Replacing `notes` therefore
 * destroys the only record of the number.
 *
 * Extracted so the three writers that touch notes — recording a result,
 * completing a preservation cycle, and ending a cycle — cannot drift apart.
 */

export function mergeOutcomeNotes({
  existing,
  added,
}: {
  /** What the outcome already stores, if anything. */
  existing?: string | null;
  /** What this write wants to add, if anything. */
  added?: string | null;
}): string | null {
  const kept = existing?.trim() ?? "";
  const addition = added?.trim() ?? "";

  if (!addition) return kept || null;

  // Line-wise rather than substring: a genuinely new note that happens to be a
  // fragment of an existing line — "low response" against "Cancelled due to low
  // response" — must not be silently dropped.
  const existingLines = kept
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (existingLines.includes(addition)) return kept || null;

  return [kept, addition].filter(Boolean).join("\n");
}
